import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Lock,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standalone screener page at /screener/:study_id
 * Renders questions one screen at a time, validates, submits to the backend
 * and shows the result (qualified -> redirect to interest form,
 * disqualified -> polite decline + register CTA).
 */
export default function ScreenerPage() {
  const { study_id: studyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [study, setStudy] = useState(null);
  const [screener, setScreener] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { qualified, reason, response_id }
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/screener/${studyId}`)
      .then((r) => {
        if (!cancelled) {
          setStudy(r.data.study);
          setScreener(r.data.screener);
        }
      })
      .catch(() => {
        if (!cancelled)
          toast.error("This screener isn't available right now.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  const setAnswer = (qid, value) =>
    setAnswers((prev) => ({ ...prev, [qid]: value }));

  const validate = () => {
    const e = {};
    (screener?.questions || []).forEach((q) => {
      if (q.required === false) return;
      const a = answers[q.id];
      if (q.type === "multiple_choice") {
        if (!Array.isArray(a) || a.length === 0) e[q.id] = "Required";
      } else if (q.type === "grid") {
        const rows = q.rows || [];
        if (!a || rows.some((r) => !a[r.id])) e[q.id] = "Answer every row";
      } else if (a === undefined || a === null || a === "") {
        e[q.id] = "Required";
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please answer every required question.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/screener/${studyId}/respond`, {
        answers,
      });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--charcoal)]">
        <Loader2 size={28} className="text-[var(--lime)] animate-spin" />
      </div>
    );
  }

  if (!study || !screener) {
    return <ScreenerUnavailable />;
  }

  if (result) {
    return result.qualified ? (
      <ScreenerQualified
        study={study}
        responseId={result.response_id}
        audience={result.audience}
      />
    ) : (
      <ScreenerDisqualified study={study} />
    );
  }

  return (
    <ScreenerLayout study={study}>
      <form
        onSubmit={submit}
        className="space-y-8"
        data-testid="screener-form"
      >
        {(screener.questions || []).map((q, idx) => (
          <QuestionRenderer
            key={q.id}
            index={idx}
            question={q}
            value={answers[q.id]}
            onChange={(v) => setAnswer(q.id, v)}
            error={errors[q.id]}
          />
        ))}

        <div className="flex justify-end pt-4 border-t border-[var(--light-border)]">
          <button
            type="submit"
            disabled={submitting}
            className="btn-teal inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="screener-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Checking…
              </>
            ) : (
              <>
                Submit
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </form>
    </ScreenerLayout>
  );
}

// ============================================================
// Layout (no nav / footer , standalone tab)
// ============================================================
function ScreenerLayout({ study, children }) {
  return (
    <div className="min-h-screen bg-[var(--bone-soft)]">
      {/* Header strip */}
      <header className="bg-[var(--charcoal)] grain-dark py-6">
        <div className="container-main flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[var(--lime)] flex items-center justify-center">
            <span className="text-[var(--charcoal)] font-bold font-['Outfit']">
              M
            </span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[var(--lime)] font-semibold">
              MediTalk · Screener
            </p>
            <p className="text-white text-sm">
              Quick eligibility check for: <strong>{study.title}</strong>
            </p>
          </div>
        </div>
      </header>

      <main className="container-main py-10 md:py-14 max-w-2xl">
        <div className="bg-white rounded-2xl border border-[var(--light-border)] shadow-sm p-6 md:p-8">
          <p className="text-sm text-[var(--text-muted)] mb-6 flex items-start gap-2">
            <Lock size={14} className="text-[var(--teal)] mt-0.5 flex-shrink-0" />
            <span>
              Answers stay private. We only use them to check whether this
              study is a good fit for you.
            </span>
          </p>
          {children}
        </div>
      </main>
    </div>
  );
}

// ============================================================
// QuestionRenderer
// ============================================================
function QuestionRenderer({ index, question, value, onChange, error }) {
  const num = index + 1;
  return (
    <div data-testid={`screener-q-${question.id}`}>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">
          Q{num}
        </span>
        {question.required !== false && (
          <span className="text-xs text-red-600 font-medium">required</span>
        )}
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
      <h3 className="font-['Outfit'] font-semibold text-[var(--charcoal)] text-lg leading-snug mb-1">
        {question.label || `Question ${num}`}
      </h3>
      {question.helper && (
        <p className="text-sm text-[var(--text-muted)] mb-3">
          {question.helper}
        </p>
      )}
      <div className="mt-3">
        <FieldByType
          question={question}
          value={value}
          onChange={onChange}
          error={!!error}
        />
      </div>
    </div>
  );
}

function FieldByType({ question, value, onChange, error }) {
  const cls = `w-full h-11 px-4 rounded-lg border bg-white text-[var(--charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--teal)]/20 ${
    error
      ? "border-red-300 focus:border-red-400"
      : "border-[var(--light-border)] focus:border-[var(--teal)]"
  }`;

  switch (question.type) {
    case "yes_no":
      return (
        <div className="flex gap-3">
          {["yes", "no"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              data-testid={`q-${question.id}-${v}`}
              className={`flex-1 py-3 rounded-xl border-2 font-semibold capitalize transition-all ${
                value === v
                  ? "border-[var(--teal)] bg-[var(--teal-soft)] text-[var(--teal-dark)]"
                  : "border-[var(--light-border)] bg-white text-[var(--charcoal)] hover:border-[var(--charcoal)]"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      );

    case "single_choice":
      return (
        <div className="space-y-2">
          {(question.options || []).map((o) => (
            <label
              key={o.id}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                value === o.id
                  ? "border-[var(--teal)] bg-[var(--teal-soft)]/40"
                  : "border-[var(--light-border)] bg-white hover:border-[var(--charcoal)]"
              }`}
            >
              <input
                type="radio"
                name={question.id}
                value={o.id}
                checked={value === o.id}
                onChange={() => onChange(o.id)}
                className="h-4 w-4 text-[var(--teal)]"
                data-testid={`q-${question.id}-${o.id}`}
              />
              <span className="text-sm text-[var(--charcoal)]">{o.label}</span>
            </label>
          ))}
        </div>
      );

    case "multiple_choice":
      return (
        <div className="space-y-2">
          {(question.options || []).map((o) => {
            const selected = Array.isArray(value) && value.includes(o.id);
            return (
              <label
                key={o.id}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  selected
                    ? "border-[var(--teal)] bg-[var(--teal-soft)]/40"
                    : "border-[var(--light-border)] bg-white hover:border-[var(--charcoal)]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => {
                    const next = new Set(value || []);
                    if (e.target.checked) next.add(o.id);
                    else next.delete(o.id);
                    onChange(Array.from(next));
                  }}
                  className="h-4 w-4 text-[var(--teal)] rounded"
                  data-testid={`q-${question.id}-${o.id}`}
                />
                <span className="text-sm text-[var(--charcoal)]">{o.label}</span>
              </label>
            );
          })}
        </div>
      );

    case "number":
    case "age":
      return (
        <input
          type="number"
          inputMode="numeric"
          className={cls}
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? "" : Number(e.target.value))
          }
          data-testid={`q-${question.id}-input`}
        />
      );

    case "date":
      return (
        <input
          type="date"
          className={cls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`q-${question.id}-input`}
        />
      );

    case "grid":
      return <GridField question={question} value={value} onChange={onChange} />;

    default:
      return (
        <input
          type="text"
          className={cls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`q-${question.id}-input`}
        />
      );
  }
}

function GridField({ question, value = {}, onChange }) {
  const rows = question.rows || [];
  const cols = question.options || [];
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full border-separate border-spacing-y-2 text-sm">
        <thead>
          <tr>
            <th />
            {cols.map((c) => (
              <th
                key={c.id}
                className="px-2 py-1 text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] text-center"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="bg-white border border-[var(--light-border)] rounded-xl"
            >
              <td className="px-3 py-2 font-medium text-[var(--charcoal)] border-y border-l border-[var(--light-border)] rounded-l-xl">
                {r.label}
              </td>
              {cols.map((c, i) => {
                const selected = value[r.id] === c.id;
                const last = i === cols.length - 1;
                return (
                  <td
                    key={c.id}
                    className={`px-3 py-2 text-center border-y border-[var(--light-border)] ${
                      last ? "border-r rounded-r-xl" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name={`${question.id}_${r.id}`}
                      checked={selected}
                      onChange={() =>
                        onChange({ ...(value || {}), [r.id]: c.id })
                      }
                      className="h-4 w-4 text-[var(--teal)]"
                      data-testid={`q-${question.id}-${r.id}-${c.id}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Result states
// ============================================================
function ScreenerQualified({ study, responseId, audience }) {
  // Send qualified people straight back to the homepage of the right page,
  // with a flag to auto-open the contact form on the study modal.
  return (
    <ScreenerLayout study={study}>
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--lime)]/20 mb-5">
          <CheckCircle2 size={32} className="text-[var(--teal)]" />
        </div>
        <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl md:text-3xl mb-3">
          Looks like a good fit.
        </h2>
        <p className="text-[var(--text-muted)] mb-6 max-w-md mx-auto">
          You're eligible for this study. Head back to the study page and leave
          your contact details so we can call you to confirm.
        </p>
        <Link
          to={
            audience === "caregiver"
              ? `/caregiver?study=${study.id}&qualified=${responseId}`
              : `/patient?study=${study.id}&qualified=${responseId}`
          }
          className="btn-teal inline-flex items-center justify-center gap-2"
          data-testid="screener-back-to-study"
        >
          Continue to contact form
          <ArrowRight size={18} />
        </Link>
      </div>
    </ScreenerLayout>
  );
}

function ScreenerDisqualified({ study }) {
  return (
    <ScreenerLayout study={study}>
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-5">
          <AlertTriangle size={28} className="text-amber-600" />
        </div>
        <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl md:text-3xl mb-3">
          This one isn't a fit, but thank you.
        </h2>
        <p className="text-[var(--text-muted)] mb-6 max-w-md mx-auto leading-relaxed">
          Based on your answers, this particular study isn't right for you.
          That doesn't mean other studies won't be! If you register on the
          MediTalk platform, we'll automatically match you to studies you
          qualify for, so you'll never miss an opportunity.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/register"
            className="btn-teal inline-flex items-center justify-center gap-2"
            data-testid="screener-register-cta"
          >
            Register for future studies
            <ArrowRight size={18} />
          </Link>
          <Link
            to="/"
            className="btn-secondary-light inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>
        </div>
      </div>
    </ScreenerLayout>
  );
}

function ScreenerUnavailable() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bone-soft)] p-6">
      <div className="max-w-md text-center bg-white rounded-2xl border border-[var(--light-border)] p-8">
        <AlertTriangle size={28} className="text-amber-600 mx-auto mb-4" />
        <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl mb-2">
          Screener unavailable
        </h2>
        <p className="text-[var(--text-muted)] mb-5">
          This screener isn't available right now. The study may have closed,
          or the link may be incorrect.
        </p>
        <Link to="/" className="btn-teal inline-flex items-center gap-2">
          Back to MediTalk
        </Link>
      </div>
    </div>
  );
}
